import type { Tag } from '@projektverwaltung/shared';
/**
 * Tag-Picker (F6) — Tags am Projekt anhängen/entfernen. Wiederverwendbar
 * (Project Detail; optional später Inline an Karte). Respektiert Scope-Regel
 * (global ODER passendes Segment) und Reader-Mode (read-only).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';

function chipStyle(color: string | null | undefined): React.CSSProperties {
  // Tag.color ist optional (Hex). Ohne Farbe neutraler Chip über Tokens.
  if (!color) {
    return {
      background: 'var(--surface-2)',
      color: 'var(--ink-2)',
      border: '1px solid var(--line-soft)',
    };
  }
  return { background: `${color}22`, color, border: `1px solid ${color}` };
}

export function TagPicker({
  projectId,
  segment,
  canEdit,
}: {
  projectId: string;
  segment: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const projectTagsQuery = useQuery({
    queryKey: ['project-tags', projectId],
    queryFn: () => api.listProjectTags(projectId),
  });
  const allTagsQuery = useQuery({
    queryKey: ['tags', 'all'],
    queryFn: () => api.listTags(),
  });

  const attached = projectTagsQuery.data?.items ?? [];
  const attachedIds = new Set(attached.map((tg) => tg.tag_id));
  // Scope: global (scope_segment null) ODER passendes Segment; und noch nicht angehängt.
  const available = (allTagsQuery.data?.items ?? []).filter(
    (tg: Tag) =>
      !attachedIds.has(tg.tag_id) && (tg.scope_segment === null || tg.scope_segment === segment),
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['project-tags', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['v2-board-projects'] });
    void queryClient.invalidateQueries({ queryKey: ['v2-atlas-projects', 'all'] });
  };

  const attach = useMutation({
    mutationFn: (tagId: string) => api.attachTag(projectId, tagId),
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        t('v2.tags.attach_failed', 'Tag konnte nicht angehängt werden'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });
  const detach = useMutation({
    mutationFn: (tagId: string) => api.detachTag(projectId, tagId),
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        t('v2.tags.detach_failed', 'Tag konnte nicht entfernt werden'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {attached.map((tg) => (
        <span
          key={tg.tag_id}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px]"
          style={{ ...chipStyle(tg.color), fontFamily: 'var(--f-mono)' }}
        >
          {tg.name}
          {canEdit && (
            <button
              type="button"
              onClick={() => detach.mutate(tg.tag_id)}
              disabled={detach.isPending}
              title={t('v2.tags.remove', 'Tag entfernen')}
              className="leading-none"
              style={{ cursor: 'pointer', opacity: detach.isPending ? 0.5 : 1 }}
            >
              ✕
            </button>
          )}
        </span>
      ))}
      {attached.length === 0 && !canEdit && (
        <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
          {t('v2.tags.none', 'Keine Tags')}
        </span>
      )}
      {canEdit && (
        <select
          value=""
          onChange={(e) => {
            const id = e.target.value;
            if (id) attach.mutate(id);
          }}
          disabled={attach.isPending || available.length === 0}
          title={
            !canEdit
              ? t('v2.reader.tooltip', 'Sie haben Leserechte')
              : t('v2.tags.add', 'Tag hinzufügen')
          }
          className="text-[11px] px-2 py-0.5 rounded outline-none"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
            fontFamily: 'var(--f-mono)',
            cursor: available.length === 0 ? 'not-allowed' : 'pointer',
            opacity: available.length === 0 ? 0.6 : 1,
          }}
        >
          <option value="" disabled>
            {available.length === 0
              ? t('v2.tags.none_available', 'keine weiteren Tags')
              : t('v2.tags.add', '+ Tag')}
          </option>
          {available.map((tg) => (
            <option key={tg.tag_id} value={tg.tag_id}>
              {tg.name}
              {tg.scope_segment === null ? ' (global)' : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
